import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreWrapper } from 'stores/StoreHelper';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { SettingItems } from 'types/CommonTypes';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '@mui/material';

const CleansingColumnSettingsModal = ({
  items,
  onSave,
}: {
  items: SettingItems[];
  onSave: (columns: SettingItems[]) => void;
}) => {
  const { t } = useTranslation();
  const { uiStore } = useStoreWrapper();

  const [localSettingItems, setLocalSettingItems] = useState<SettingItems[]>(items);

  useEffect(() => {
    setLocalSettingItems(items);
  }, [items]);

  // 보존 버튼 클릭시
  const handleSave = () => {
    toast.success(t('Save Success'));
    onSave(localSettingItems);
    uiStore?.modal.close();
  };

  // 체크 박스 클릭시 토글 효과
  const handleCheckboxToggle = (id: string) => {
    setLocalSettingItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item)),
    );
  };

  // 일괄 표시/비표시 클릭시 효과
  const handleCheckboxAllChange = (isCheck: boolean) => {
    setLocalSettingItems((prevItems) => prevItems.map((item) => ({ ...item, visible: isCheck })));
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(localSettingItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // 순서 변경 후 seq 값 업데이트
    const updatedItems = items.map((item, index) => ({
      ...item,
      seq: index + 1
    }));

    setLocalSettingItems(updatedItems);
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        uiStore?.modal.close();
      }
    };

    document.addEventListener('keydown', handleEscKey, true);

    return () => {
      document.removeEventListener('keydown', handleEscKey, true);
    };
  }, [uiStore]);

  return (
    <Container>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="settings-list">
          {(provided) => (
            <SettingsList {...provided.droppableProps} ref={provided.innerRef}>
              {localSettingItems
                ?.sort((a, b) => (a.seq || 0) - (b.seq || 0))
                .map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided) => (
                      <SettingsRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <DragHandle>⋮</DragHandle>
                        <SettingsLabel>{t(item.label)}</SettingsLabel>
                        <SettingsCheckboxInput
                          type="checkbox"
                          checked={item.visible}
                          onClick={() => handleCheckboxToggle(item.id)}
                        />
                      </SettingsRow>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </SettingsList>
          )}
        </Droppable>
      </DragDropContext>
      <ButtonContainer>
        <VisiblityToggleButtonGroup>
          <VisiblityToggleButton onClick={() => handleCheckboxAllChange(true)}>
            {t('All Visible')}
          </VisiblityToggleButton>
          <VisiblityToggleButton onClick={() => handleCheckboxAllChange(false)}>
            {t('All Invisible')}
          </VisiblityToggleButton>
        </VisiblityToggleButtonGroup>
        <Button variant="contained" onClick={handleSave}>
          {t('Save')}
        </Button>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SettingsList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  margin-right: 12px;
  color: #9ca3af;
  cursor: grab;
  user-select: none;
`;

const SettingsLabel = styled.div`
  flex: 1;
  font-size: 14px;
`;

const SettingsCheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const VisiblityToggleButtonGroup = styled.div`
  display: inline-flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
`;

const VisiblityToggleButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  background-color: white;
  border: none;
  border-right: 1px solid #ccc;
  cursor: pointer;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  &:active {
    background-color: #e5e5e5;
  }
`;

export default CleansingColumnSettingsModal;
